// client/src/components/profissionais/tabs/HorariosTab.js
import React, { useState, useMemo } from 'react';
import { useHorarios, useMutateHorarios } from '../../../hooks/useHorarios';
import { useUnidadesPorConta } from '../../../hooks/useUnidades'; 
import { Clock, PlusCircle, ArrowLeft, Loader2, Info, RefreshCcw } from 'lucide-react';
// Reaproveitando os componentes já criados na pasta de configurações
import HorarioForm from '../../configuracoes/horarios/HorarioForm';
import HorarioRow from '../../configuracoes/horarios/HorarioRow';

// ✅ FUNÇÃO BLINDADA PARA EXTRAIR ID
const getSafeId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const HorariosTab = ({ user, contextData }) => {
    const targetUserId = getSafeId(user);
    const targetAccountId = getSafeId(contextData?.contaId);

    // Estados de controle da interface
    const [showForm, setShowForm] = useState(false);
    const [currentHorario, setCurrentHorario] = useState(null);

    // Buscar horários APENAS deste usuário
    const paramsBuscaHorarios = useMemo(() => ({
        tipoEntidade: 'User',
        entidadeAgendavel: targetUserId,
    }), [targetUserId]);

    const { 
        data: horariosData, 
        isLoading: horarioLoading, 
        refetch: refetchHorarios, 
        isFetching 
    } = useHorarios(paramsBuscaHorarios, { 
        enabled: !!targetUserId 
    });

    const { deleteHorario } = useMutateHorarios();
    const isMutating = deleteHorario.isPending;

    // Processar dados dos horários
    const horarios = horariosData || [];
    const horariosSemanais = useMemo(() => Array.isArray(horarios) ? horarios.filter(h => h.tipo === 'SEMANAL') : [], [horarios]);
    const datasEspecificas = useMemo(() => Array.isArray(horarios) ? horarios.filter(h => h.tipo === 'DATA_UNICA') : [], [horarios]);

    // Obter todas as unidades da conta
    const { data: unidadesDataResponse, isLoading: unidadesLoading } = useUnidadesPorConta(targetAccountId, { 
        enabled: !!targetAccountId 
    });

    // Formatando as unidades disponíveis
    const unidadesDisponiveis = useMemo(() => {
        const arrayUnidades = Array.isArray(unidadesDataResponse?.data) ? unidadesDataResponse.data : [];
        return arrayUnidades.map(u => ({ ...u, _id: String(u._id || u.id), nome: u.nome }));
    }, [unidadesDataResponse]);

    // Handlers
    const handleToggleForm = () => { 
        if (showForm) setCurrentHorario(null); 
        setShowForm(p => !p); 
    };
    
    const handleEdit = (horario) => { 
        setCurrentHorario(horario); 
        setShowForm(true); 
    };
    
    const handleDelete = (horarioId) => { 
        if(window.confirm('Tem certeza que deseja excluir este horário?')) {
            deleteHorario.mutate(horarioId); 
        }
    };

    // Bloqueio se o usuário não estiver totalmente carregado
    if (!targetUserId) {
        return (
            <div className="form-content">
                <div className="form-header">
                    <div className="flex-1">
                        <h3 className="form-title flex items-center mb-0">
                            <Clock size={20} className="inline mr-2 text-primary" />
                            Horários de Atendimento
                        </h3>
                    </div>
                </div>
                <div className="form-tabs-content p-0 mt-4">
                    <div className="panel-state-wrapper panel-info border border-blue-200">
                        <Info size={24} className="icon text-blue-500"/>
                        <span className="text-gray-600">
                            Identificação do profissional não encontrada. Tente carregar novamente.
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="form-content" disabled={isMutating}>
            {/* CABEÇALHO PADRONIZADO */}
            <div className="form-header flex items-center justify-between mb-4">
                <div className="flex-1">
                    <h3 className="form-title flex items-center mb-0">
                        <Clock size={20} className="inline mr-2 text-primary" />
                        Horários de Atendimento
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Defina os dias, horários e intervalos de trabalho deste profissional por unidade.
                    </p>
                </div>
                
                {!showForm ? (
                    <div className="flex gap-2">
                        <button 
                            type="button" 
                            onClick={() => refetchHorarios()} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700" 
                            title="Atualizar Lista"
                            disabled={isFetching}
                        >
                            <RefreshCcw size={16} className={`mr-2 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
                            Atualizar
                        </button>
                        <button 
                            type="button" 
                            onClick={handleToggleForm} 
                            className="action-button action-button-primary bg-primary text-white shadow-sm"
                            disabled={unidadesLoading}
                        >
                            <PlusCircle size={18} className="mr-2" />
                            Adicionar Regra
                        </button>
                    </div>
                ) : (
                    <button 
                        type="button" 
                        onClick={handleToggleForm}
                        className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                    >
                        <ArrowLeft size={16} className="mr-2 text-gray-500" /> Voltar para lista
                    </button>
                )}
            </div>

            <div className="form-tabs-content p-0 mt-4 border-t border-gray-100 pt-6">
                {!showForm ? (
                    <div className="table-wrapper border border-gray-200 rounded-lg overflow-hidden">
                        {horarioLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 size={32} className="animate-spin text-primary" />
                            </div>
                        ) : (
                            <table className="styled-table w-full">
                                <thead>
                                    <tr>
                                        <th>Dia / Data</th>
                                        <th>Tipo</th>
                                        <th>Unidade</th>
                                        <th>Início</th>
                                        <th>Fim</th>
                                        <th>Intervalos (Pausas)</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {horariosSemanais.length > 0 && horariosSemanais.map(h => (
                                        <HorarioRow 
                                            key={h._id} 
                                            horario={h} 
                                            onEdit={handleEdit} 
                                            onDelete={handleDelete} 
                                            isMutating={isMutating} 
                                        />
                                    ))}
                                    
                                    {datasEspecificas.length > 0 && datasEspecificas.map(h => (
                                        <HorarioRow 
                                            key={h._id} 
                                            horario={h} 
                                            onEdit={handleEdit} 
                                            onDelete={handleDelete} 
                                            isMutating={isMutating} 
                                        />
                                    ))}
                                    
                                    {(!horarios || horarios.length === 0) && (
                                        <tr>
                                            <td colSpan="7" className="text-center py-12 text-gray-500 bg-gray-50 border-t border-gray-100">
                                                <Clock size={32} className="mx-auto text-gray-300 mb-3" />
                                                Nenhum horário cadastrado para este profissional.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 relative">
                        {/* Reutilizando o formulário exato da página de configurações globais */}
                        <HorarioForm 
                            entidadeId={targetUserId}
                            tipoEntidade="User"
                            onCloseForm={handleToggleForm} 
                            current={currentHorario}
                            clearCurrent={() => setCurrentHorario(null)}
                            horarios={horarios}
                            unidadesDisponiveis={unidadesDisponiveis} // Para o usuário, listamos as unidades
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default HorariosTab;